<template>
	<div id="wrapper">
		<SidebarView :fixedAssets="true" :fixedAssetsVisible="true" :assetRegisterReport="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Asset Register Report</h6>
							<b-button class="akkurate-green-btn btn-size text-white" :disabled="exporting" @click="exportExcel">
								<b-spinner v-if="exporting" small class="me-1" />
								Export Excel
							</b-button>
						</div>
						<div class="card-body">
							<div class="row g-2 mb-3 align-items-end">
								<div class="col-md-3">
									<label class="akkurate-dark small">Branch</label>
									<multiselect v-model="filters.branch" :options="branches" label="name" track-by="id" placeholder="All" class="field-container" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Category</label>
									<multiselect v-model="filters.category" :options="categories" label="name" track-by="id" placeholder="All" class="field-container" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Assigned to</label>
									<multiselect v-model="filters.assigned_to" :options="users" label="full_name" track-by="id" placeholder="All" class="field-container" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Status</label>
									<b-form-select v-model="filters.status" :options="statusOptions" class="field-container" />
								</div>
								<div class="col-md-6">
									<label class="akkurate-dark small">Search</label>
									<b-form-input v-model="filters.q" class="field-container" placeholder="Search..." @keyup.enter="load" />
								</div>
								<div class="col-md-auto d-flex gap-2">
									<b-button class="akkurate-green-btn btn-size text-white" @click="load">
										<font-awesome-icon class="me-1" :icon="['fas', 'search']" />
										Apply
									</b-button>
									<b-button variant="outline-secondary" class="btn-size" @click="resetFilters">Reset</b-button>
								</div>
							</div>

							<div v-if="error" class="alert alert-danger py-2 small mb-2">{{ error }}</div>

							<b-table
								striped
								bordered
								responsive
								:busy="busy"
								:items="rows"
								:fields="fields"
								show-empty
								class="table_text_size"
							>
								<template #table-busy>
									<div class="text-center text-danger my-2">
										<b-spinner class="align-middle" />
										<strong>Loading...</strong>
									</div>
								</template>
							</b-table>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "AssetRegisterReport",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			busy: false,
			exporting: false,
			error: "",
			rows: [],
			fields: [
				{ key: "asset_tag", label: "Tag" },
				{ key: "asset_name", label: "Asset" },
				{ key: "category", label: "Category" },
				{ key: "branch_id", label: "Branch" },
				{ key: "assigned_to", label: "Assigned to" },
				{ key: "purchase_date", label: "Purchase date" },
				{ key: "purchase_cost", label: "Purchase cost" },
				{ key: "salvage_value", label: "Salvage value" },
				{ key: "depreciation_method", label: "Depreciation method" },
				{ key: "useful_life_months", label: "Useful life (months)" },
				{ key: "accumulated_depreciation", label: "Acc. depreciation" },
				{ key: "net_book_value", label: "NBV" },
				{ key: "status", label: "Status" },
				{ key: "condition_status", label: "Condition" },
			],
			branches: [],
			categories: [],
			users: [],
			filters: { branch: null, category: null, status: "", assigned_to: null, q: "" },
			statusOptions: [
				{ value: "", text: "All" },
				{ value: "active", text: "Active" },
				{ value: "in_use", text: "In use" },
				{ value: "under_maintenance", text: "Under maintenance" },
				{ value: "disposed", text: "Disposed" },
			],
		};
	},
	created() {
		this.loadDropdowns();
		this.load();
	},
	methods: {
		async loadDropdowns() {
			try {
				const [br, cat, us] = await Promise.all([
					ApiService.get("/branches/dropdown").catch(() => ({ data: {} })),
					ApiService.query("/asset-categories", { params: { per_page: 100, is_active: 1 } }).catch(() => ({ data: {} })),
					ApiService.get("/users/dropdown").catch(() => ({ data: {} })),
				]);
				this.branches = br.data?.branches || [];
				this.categories = cat.data?.data || cat.data || [];
				this.users = us.data?.data || us.data || [];
			} catch (e) {
				/* non-blocking */
			}
		},
		queryParams() {
			return {
				branch_id: this.filters.branch?.id,
				category_id: this.filters.category?.id,
				status: this.filters.status || undefined,
				assigned_to: this.filters.assigned_to?.id,
				q: this.filters.q || undefined,
			};
		},
		resetFilters() {
			this.filters = { branch: null, category: null, status: "", assigned_to: null, q: "" };
			this.load();
		},
		async load() {
			this.busy = true;
			this.error = "";
			this.$Progress.start();
			try {
				const res = await ApiService.query("/reports/assets/register", { params: { ...this.queryParams(), per_page: 30 } });
				const body = res.data;
				this.rows = body.data || body || [];
			} catch (e) {
				this.rows = [];
				this.error = e.response?.data?.message || "Could not load report.";
			} finally {
				this.busy = false;
				this.$Progress.finish();
			}
		},
		async exportExcel() {
			this.exporting = true;
			this.error = "";
			try {
				const res = await ApiService.query("/reports/assets/register/export-excel", { params: this.queryParams() });
				const url = res.data?.download_url;
				if (url) {
					window.open(url, "_blank", "noopener,noreferrer");
					toast.fire({ icon: "success", title: res.data?.message || "Export ready" });
				} else {
					this.error = "Export did not return a download URL.";
				}
			} catch (e) {
				if (e.response?.status === 422 && e.response?.data?.message) {
					this.error = e.response.data.message;
				} else {
					this.error = e.response?.data?.message || "Export failed.";
				}
			} finally {
				this.exporting = false;
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

